import type { PlatformInfo } from "../api";

/**
 * Real brand marks for the connected platforms, each drawn as a self-contained
 * rounded-square badge (so it replaces the old colored-square + text-glyph).
 * Brand paths are the standard single-colour logo glyphs (viewBox 0 0 24 24),
 * centred inside a 28-unit badge with padding. Unknown platforms fall back to
 * the platform's accent colour + text glyph.
 */
export function PlatformIcon({
  platform,
  size = 26,
}: {
  platform: PlatformInfo;
  size?: number;
}) {
  const common = {
    width: size,
    height: size,
    viewBox: "0 0 28 28",
    style: { display: "block", flexShrink: 0 } as const,
    "aria-hidden": true,
  };
  // 24-unit brand glyph centred in the 28 badge with 6px padding (scale 16/24).
  const markTransform = "translate(6 6) scale(0.6667)";

  switch (platform.id) {
    case "youtube":
      return (
        <svg {...common}>
          <rect width="28" height="28" rx="7" fill="#FF0000" />
          <path d="M11 9v10l8.5-5z" fill="#fff" />
        </svg>
      );

    case "tiktok": {
      // Chromatic note: cyan echo up-left, magenta echo down-right, white on top.
      const note =
        "M12.53.02C13.84 0 15.14.01 16.44 0c.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03C3.23 21.75 1.81 19.57 1.6 17.23c-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z";
      return (
        <svg {...common}>
          <rect width="28" height="28" rx="7" fill="#010101" />
          <g transform={markTransform}>
            <path d={note} fill="#25F4EE" transform="translate(-1 -0.6)" />
            <path d={note} fill="#FE2C55" transform="translate(1 0.6)" />
            <path d={note} fill="#fff" />
          </g>
        </svg>
      );
    }

    case "instagram":
      return (
        <svg {...common}>
          <defs>
            <radialGradient id="ig-grad" cx="30%" cy="107%" r="150%">
              <stop offset="0%" stopColor="#fdf497" />
              <stop offset="5%" stopColor="#fdf497" />
              <stop offset="45%" stopColor="#fd5949" />
              <stop offset="60%" stopColor="#d6249f" />
              <stop offset="90%" stopColor="#285AEB" />
            </radialGradient>
          </defs>
          <rect width="28" height="28" rx="7" fill="url(#ig-grad)" />
          <g transform={markTransform} fill="#fff">
            <path d="M12 2.16c3.2 0 3.58.01 4.85.07 1.17.05 1.8.25 2.23.41.56.22.96.48 1.38.9.42.42.68.82.9 1.38.16.42.36 1.06.41 2.23.06 1.27.07 1.65.07 4.85s-.01 3.58-.07 4.85c-.05 1.17-.25 1.8-.41 2.23-.22.56-.48.96-.9 1.38-.42.42-.82.68-1.38.9-.42.16-1.06.36-2.23.41-1.27.06-1.65.07-4.85.07s-3.58-.01-4.85-.07c-1.17-.05-1.8-.25-2.23-.41a3.7 3.7 0 0 1-1.38-.9 3.7 3.7 0 0 1-.9-1.38c-.16-.42-.36-1.06-.41-2.23C2.17 15.58 2.16 15.2 2.16 12s.01-3.58.07-4.85c.05-1.17.25-1.8.41-2.23.22-.56.48-.96.9-1.38.42-.42.82-.68 1.38-.9.42-.16 1.06-.36 2.23-.41C8.42 2.17 8.8 2.16 12 2.16m0-2.16C8.74 0 8.33.01 7.05.07 5.78.13 4.9.33 4.14.63c-.79.31-1.46.72-2.13 1.38A5.9 5.9 0 0 0 .63 4.14C.33 4.9.13 5.78.07 7.05.01 8.33 0 8.74 0 12s.01 3.67.07 4.95c.06 1.27.26 2.15.56 2.91.31.79.72 1.46 1.38 2.13.67.66 1.34 1.07 2.13 1.38.76.3 1.64.5 2.91.56C8.33 23.99 8.74 24 12 24s3.67-.01 4.95-.07c1.27-.06 2.15-.26 2.91-.56.79-.31 1.46-.72 2.13-1.38.66-.67 1.07-1.34 1.38-2.13.3-.76.5-1.64.56-2.91.06-1.28.07-1.69.07-4.95s-.01-3.67-.07-4.95c-.06-1.27-.26-2.15-.56-2.91a5.9 5.9 0 0 0-1.38-2.13A5.9 5.9 0 0 0 19.86.63c-.76-.3-1.64-.5-2.91-.56C15.67.01 15.26 0 12 0z" />
            <path d="M12 5.84A6.16 6.16 0 1 0 18.16 12 6.16 6.16 0 0 0 12 5.84m0 10.16A4 4 0 1 1 16 12a4 4 0 0 1-4 4z" />
            <circle cx="18.41" cy="5.59" r="1.44" />
          </g>
        </svg>
      );

    case "facebook":
      // Official brand blue + the "f" mark. The mark is authored on the brand's
      // 320×512 grid, so it carries its own transform to fit the 24-unit glyph
      // box (scale 24/512, nudged right to centre the 15-unit-wide "f").
      return (
        <svg {...common}>
          <rect width="28" height="28" rx="7" fill="#1877F2" />
          <g transform={markTransform}>
            <path
              d="M279.14 288l14.22-92.66h-88.91v-60.13c0-25.35 12.42-50.06 52.24-50.06h40.42V6.26S260.43 0 225.36 0c-73.22 0-121.08 44.38-121.08 124.72v70.62H22.89V288h81.39v224h100.17V288z"
              transform="translate(4.5 0) scale(0.046875)"
              fill="#fff"
            />
          </g>
        </svg>
      );

    case "x":
      return (
        <svg {...common}>
          <rect width="28" height="28" rx="7" fill="#000" />
          <g transform={markTransform}>
            <path
              d="M18.9 1.15h3.68l-8.04 9.19L24 22.85h-7.41l-5.8-7.58-6.64 7.58H.47l8.6-9.83L0 1.15h7.59l5.24 6.93zm-1.29 19.49h2.04L6.49 3.24H4.3z"
              fill="#fff"
            />
          </g>
        </svg>
      );

    default:
      // Fallback: accent square + text glyph, scaled to the badge.
      return (
        <svg {...common}>
          <rect width="28" height="28" rx="7" fill={platform.accent} />
          <text
            x="14"
            y="15"
            fill="#fff"
            fontSize="14"
            fontWeight="700"
            textAnchor="middle"
            dominantBaseline="central"
          >
            {platform.glyph}
          </text>
        </svg>
      );
  }
}
